import { Injectable, signal, WritableSignal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { User } from '@fc-core/models';
import {
  catchError,
  distinctUntilChanged,
  map,
  switchMap,
  take,
} from 'rxjs/operators';
import { BehaviorSubject, Observable, of, timer } from 'rxjs';
import moment from 'moment';
import { environment } from '../../environments/environment';
import {
  MomentService,
  setDefaultTimezone,
} from '@fc-core/services/moment.service';
import { AppSettingsService } from '@fc-core/services/app-settings.service';
import { Router } from '@angular/router';
import { getCurrentScope } from '@sentry/angular';
import { TokenData } from './models/token-data';
import { decodeTokenData } from './utils/token-utils';
import { AuthCustomer } from './models/auth-customer';
import { BackendResponse } from '@fc-core/models/response.model';
import { NotificationService } from '@fc-core/services/notification.service';
import { inject } from '@angular/core';

export function getToken() {
  return localStorage.getItem('token');
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private notifications = inject(NotificationService);
  token: string | null;
  refreshTokenKey: string | null;
  readonly tokenData$: WritableSignal<TokenData | null> = signal(null);

  loginData: {
    access: string;
    refresh: string;
    user: User;
  };
  options = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };
  loggedInSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  loggedInObservable: Observable<boolean>;

  constructor(
    private http: HttpClient,
    private momentService: MomentService,
    private appSettings: AppSettingsService,
    private router: Router,
  ) {
    this.token = getToken();
    this.refreshTokenKey = this.currentRefreshToken;
    this.setTokenData();
    this.loggedInObservable = this.loggedInSubject.asObservable();
  }

  set tokenData(data: TokenData | null) {
    this.tokenData$.set(data);
  }

  get tokenData(): TokenData | null {
    return this.tokenData$();
  }

  get currentRefreshToken(): string | null {
    return localStorage.getItem('refresh');
  }

  get customerApiToken(): string | null {
    return localStorage.getItem('customerApiToken');
  }

  get tokenExpiration(): number | undefined {
    if (!this.token) return undefined;
    const { exp } = this.decodeBase64Token(this.token);
    return moment.unix(exp).diff(moment(), 'seconds');
  }

  decodeBase64Token(token: string): TokenData {
    const tokenData = decodeTokenData(token);
    if (!tokenData) throw new Error('Invalid token');
    return tokenData;
  }

  fileLogin(token: string): Observable<void> {
    return this.http.post<void>(
      `${environment.filesApi}/login`,
      {},
      {
        headers: new HttpHeaders({
          Authorization: `JWT ${token}`,
        }),
        withCredentials: true,
      },
    );
  }

  getCookie(name: string): boolean {
    return document.cookie
      .split(';')
      .some((c) => c.trim().startsWith(name + '='));
  }

  deleteCookie(name: string, path: string, domain: string) {
    if (this.getCookie(name)) {
      document.cookie =
        name +
        '=' +
        (path ? ';path=' + path : '') +
        (domain ? ';domain=' + domain : '') +
        ';expires=Thu, 01 Jan 1970 00:00:01 GMT';
    }
  }

  clearFileCookies() {
    this.deleteCookie(
      '_fl_files_t',
      '/',
      (environment.filesApi as string).replace('https://', ''),
    );
  }

  getCustomers(token: string): Observable<AuthCustomer[]> {
    return this.http
      .get<BackendResponse<AuthCustomer[]>>(
        `${environment.apiUrl}api/auth/customers/`,
        {
          headers: new HttpHeaders({
            Authorization: `JWT ${token}`,
          }),
        },
      )
      .pipe(
        map((response: BackendResponse<AuthCustomer[]>) =>
          response.results.map((customer) => ({
            customerId: customer.customerId,
            id: customer.id,
            name: customer.name,
          })),
        ),
      );
  }

  setToken(token: string, refresh: string): TokenData | null {
    localStorage.setItem('token', token);
    localStorage.setItem('refresh', refresh);
    this.token = token;
    this.refreshTokenKey = refresh;
    this.setTokenData();
    this.loggedInSubject.next(true);
    return this.tokenData;
  }

  switchCustomer(customerId: string): Observable<User | null> {
    return this.http
      .post<{
        access: string;
        refresh: string;
        user: User;
      }>(`${environment.apiUrl}api/auth/token/`, { customerId })
      .pipe(
        switchMap(
          (response: { access: string; refresh: string; user: User }) => {
            this.loginData = response;
            return this.setUpLogin(
              response.access,
              response.refresh,
              response.user,
            );
          },
        ),
      );
  }

  switchToEmployee(employee: string, customer: string): Observable<boolean> {
    return this.http
      .post<{
        access: string;
        refresh: string;
      }>(
        `${environment.apiUrl}api/auth/switch/`,
        { employee, customer },
        this.options,
      )
      .pipe(map(this.setUpCredentials.bind(this)));
  }

  switchToCustomerDev(customer: string): Observable<boolean> {
    return this.http
      .post<{
        access: string;
        refresh: string;
      }>(`${environment.apiUrl}api/auth/switch/`, { customer }, this.options)
      .pipe(map(this.setUpCredentials.bind(this)));
  }

  loginCustomerSelect(customerId: string): Observable<User | null> {
    const customerApiToken = localStorage.getItem('customerApiToken');
    if (!customerApiToken) {
      this.logout();
    }
    return this.http
      .post<{ access: string; refresh: string; user: User }>(
        `${environment.apiUrl}api/auth/token/`,
        { customerId },
        {
          headers: new HttpHeaders({
            Authorization: `JWT ${customerApiToken}`,
          }),
        },
      )
      .pipe(
        switchMap(
          (response: { access: string; refresh: string; user: User }) => {
            this.loginData = response;
            return this.setUpLogin(
              response.access,
              response.refresh,
              response.user,
            );
          },
        ),
      );
  }

  login(
    credentials: Partial<{ email: string; password: string }>,
  ): Observable<User | null> {
    const { email, password } = credentials;

    return this.http
      .post<{
        access: string;
        refresh: string;
        user: User;
      }>(
        `${environment.apiUrl}api/auth/login/`,
        { email, password },
        this.options,
      )
      .pipe(
        switchMap(
          (response: { access: string; refresh: string; user: User }) => {
            this.loginData = response;
            const decodedToken: TokenData = this.decodeBase64Token(
              response.access,
            );

            if (!decodedToken.customer_id) {
              localStorage.setItem('customerApiToken', response.access);
              this.router.navigate(['/auth/switch']);
              return of(null);
            }

            return this.setUpLogin(
              response.access,
              response.refresh,
              response.user,
            );
          },
        ),
      );
  }

  setUpLogin(
    token: string,
    refresh: string,
    user: User,
  ): Observable<User | null> {
    if (token) {
      const tokenData = this.decodeBase64Token(token);
      const platformAccess = tokenData.platform_access;
      if (
        (platformAccess?.length === 1 &&
          platformAccess.includes('employee_app')) ||
        platformAccess?.includes('no_access')
      ) {
        this.notifications.showError("You don't have access to this platform");
        return of(null);
      }
      this.setToken(token, refresh);
      return this.fileLogin(token).pipe(
        switchMap(() => {
          return this.appSettings.getEmployeeAppSettings().pipe(
            map(() => user),
            catchError(() => of(user)),
          );
        }),
      );
    } else {
      return of(null);
    }
  }

  reset(email: string): Observable<any> {
    return this.http.post(
      `${environment.apiUrl}api/auth/password/reset/`,
      { email },
      this.options,
    );
  }

  confirm(new_password: string, uid: string, token: string): Observable<any> {
    return this.http.post(
      `${environment.apiUrl}api/auth/password/reset/confirm/`,
      { new_password, uid, token },
      this.options,
    );
  }

  confirmEmailChange(
    uid: string,
    token: string,
  ): Observable<{ detail: string }> {
    return this.http.post<{ detail: string }>(
      `${environment.apiUrl}api/auth/email/change/confirm/`,
      { uid, token },
      this.options,
    );
  }

  changePassword(passwords: {
    oldPassword: string;
    newPassword1: string;
    newPassword2: string;
  }) {
    const { oldPassword, newPassword1, newPassword2 } = passwords;
    return this.http.post(
      `${environment.apiUrl}api/auth/password/change/`,
      { oldPassword, newPassword1, newPassword2 },
      this.options,
    );
  }

  setUpCredentials(
    response: { access: string; refresh: string } & Record<string, any>,
  ): boolean {
    // login successful if there's a jwt token in the response
    const token = response.access || response['accessToken'];
    const refresh = response.refresh || response['accessRefresh'];
    if (token) {
      localStorage.setItem('token', token);
      localStorage.setItem('refresh', refresh);

      // set token property
      this.setTokenData();
      location.assign('/map');
      return true;
    } else {
      // return false to indicate failed login
      return false;
    }
  }

  logoutWithoutRedirect(): void {
    this.token = null;
    localStorage.removeItem('token');
    localStorage.removeItem('refresh');
    localStorage.removeItem('customerApiToken');
    this.appSettings.clearCachedEmployeeAppSettings();
    this.loggedInSubject.next(false);
    this.clearFileCookies();
  }

  logout(): Promise<boolean> {
    this.logoutWithoutRedirect();
    return this.router.navigate(['/auth/login']);
  }

  loggedIn$(): Observable<boolean> {
    this.checkToken();
    return this.loggedInObservable.pipe(distinctUntilChanged());
  }

  loggedIn(): boolean {
    return this.checkToken();
  }

  refreshTokenRequest(): Observable<boolean> {
    if (!this.refreshTokenKey) {
      this.logout();
      return of(false);
    }
    return this.http
      .post(`${environment.apiUrl}api/auth/token/refresh/`, {
        refresh: this.refreshTokenKey,
      })
      .pipe(
        map((response: any) => {
          const token = response['access'];
          const refresh = response['refresh'];
          if (token) {
            const tokenData = this.decodeBase64Token(token);
            localStorage.setItem('token', token);
            localStorage.setItem('refresh', refresh);
            localStorage.setItem('timezone', tokenData.timezone);
            this.token = token;
            this.setTokenData();
            this.loggedInSubject.next(true);
            this.fileLogin(token);
            return token;
          }
          return null;
        }),
        catchError(() => {
          this.logout();
          return of(false);
        }),
      );
  }

  refreshToken(): void {
    const tokenExpiration = this.tokenExpiration;
    if (tokenExpiration === undefined) return;
    if (tokenExpiration <= 3600) {
      // refresh right now if expiration is less than an hour
      this.refreshTokenRequest()
        .pipe(take(1))
        .subscribe(() => console.log('refresh less then an hour'));
    } else {
      // refresh after half of the time with timer
      const source = timer(tokenExpiration / 2);
      source.subscribe(() =>
        this.refreshTokenRequest()
          .pipe(take(1))
          .subscribe(() => console.log('refreshed from timer')),
      );
    }
  }

  private setTokenData() {
    if (this.token != null) {
      try {
        this.tokenData = this.decodeBase64Token(this.token);
        this.momentService.timezone = this.tokenData.timezone;
        setDefaultTimezone(this.tokenData.timezone);
        getCurrentScope().setTag('user', `(${this.tokenData.email})`);
        getCurrentScope().setTag('customer', this.tokenData.customer_id);
      } catch (e) {
        console.error('Invalid token', e);
        this.token = null;
      }
    }
  }

  getTokenExpirationDate(token: string): Date | null {
    const decoded = this.decodeBase64Token(token);

    if (!decoded || !decoded.exp) {
      return null;
    }

    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);

    return date;
  }

  isTokenExpired(token: string | null, offsetSeconds?: number): boolean {
    if (!token || token === '') {
      return true;
    }
    const date = this.getTokenExpirationDate(token);
    offsetSeconds = offsetSeconds || 0;

    if (date === null) {
      return false;
    }

    return !(date.valueOf() > new Date().valueOf() + offsetSeconds * 1000);
  }

  private checkToken(): boolean {
    const loggedIn = !this.isTokenExpired(this.token);
    this.loggedInSubject.next(loggedIn);
    return loggedIn;
  }
}
